import { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { useRoosterStore } from '../store/useRoosterStore.js';

const DAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const DAG_LABELS = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };

function DagToggle({ waarde = [], onChange, label, hint }) {
  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-0.5">{label}</div>
      {hint && <div className="text-xs text-slate-400 mb-1.5">{hint}</div>}
      <div className="flex gap-1.5">
        {DAGEN.map(d => (
          <button
            key={d}
            type="button"
            onClick={() => {
              const nieuw = waarde.includes(d) ? waarde.filter(x => x !== d) : [...waarde, d];
              onChange(nieuw);
            }}
            className={`w-10 h-8 rounded text-xs font-medium transition ${
              waarde.includes(d)
                ? 'bg-brand-700 text-white'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {DAG_LABELS[d]}
          </button>
        ))}
      </div>
    </div>
  );
}

function DagSingle({ waarde, onChange }) {
  return (
    <div className="flex gap-1.5 mt-2">
      {['ma', 'di', 'wo', 'do', 'vr'].map(d => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          className={`w-10 h-8 rounded text-xs font-medium transition ${
            waarde === d
              ? 'bg-violet-600 text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
          }`}
        >
          {DAG_LABELS[d]}
        </button>
      ))}
    </div>
  );
}

function Toggle({ aan, onChange, label, hint }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
        {hint && <div className="text-xs text-slate-400">{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!aan)}
        className={`w-12 h-6 rounded-full transition-colors relative shrink-0 ${
          aan ? 'bg-brand-700' : 'bg-slate-300'
        }`}
      >
        <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${
          aan ? 'left-6' : 'left-0.5'
        }`} />
      </button>
    </div>
  );
}

// Per-dag beschikbaarheid in 4 standen. Terugvertaald naar de config: Vrij = vaste_vrije_dagen,
// Alleen dag = geen_late_dienst_dagen, Alleen laat = geen_dagdienst_dagen, Beschikbaar = geen ervan.
// werkdagen wordt niet meer gebruikt.
const STAND_VOLGORDE = { beschikbaar: 'vrij', vrij: 'alleen_dag', alleen_dag: 'alleen_laat', alleen_laat: 'beschikbaar' };
const STAND_STIJL = {
  beschikbaar: { label: 'dag+laat',    cls: 'bg-green-100 text-green-800 border-green-200' },
  vrij:        { label: 'vrij',        cls: 'bg-slate-100 text-slate-400 border-slate-200' },
  alleen_dag:  { label: 'alleen dag',  cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  alleen_laat: { label: 'alleen laat', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

function DagStandKiezer({ standVan, onRoteer }) {
  return (
    <div className="flex gap-1.5">
      {DAGEN.map(d => {
        const s = STAND_STIJL[standVan(d)];
        return (
          <button
            key={d}
            type="button"
            onClick={() => onRoteer(d)}
            className={`flex-1 min-w-0 rounded border px-1 py-1.5 text-center transition ${s.cls}`}
          >
            <div className="text-xs font-semibold">{DAG_LABELS[d]}</div>
            <div className="text-[10px] leading-tight">{s.label}</div>
          </button>
        );
      })}
    </div>
  );
}

const DEFAULT_MEDEWERKER = {
  naam: '',
  email: '',
  rollen: ['medewerker'],
  contract_uren: 36,
  // Leeg: geen hardcoded gemeente-key als default. De opslaan-knop blijft disabled
  // tot er minstens één gemeente is aangevinkt (zie de disabled-conditie onderaan).
  gemeenten: [],
  werkdagen: [],
  vaste_vrije_dagen: [],
  kantoor_dagen: [],
  school: null,
  dienst_voorkeur: 'late_dienst',
  alleen_dagdienst: false,
  geen_dagdienst: false,
  geen_late_dienst_dagen: [],
  weekend_rotatie: true,
  vast_rotatiepatroon: false,
  afspraken: [],
};

// Aantal roostercellen waarin `naam` voorkomt, over de twee stores die bewaarde weken
// dragen. Het rooster bewaart medewerkers op hun WEERGAVENAAM (rooster[gem][dienst][dag]
// is een namen-array), niet op mwKey — vandaar dat hernoemen die cellen losknipt.
//
// ONTDUBBELD OP WEEKIDENTITEIT. vastgelegdeWeken['2026-34'].week en de week met
// weeknummer 34 in maandroosters['2026-8'] zijn DEZELFDE week; balans.js:44-45 stelt
// datzelfde vast (zoekWeekObject pakt daarom de eerste van de twee). Zonder ontdubbeling
// telt elke dienst dubbel: op de live-data van 22 aug 2026 geeft naïef optellen 60 waar
// er 30 diensten staan. De sleutel isoJaar-weeknummer|gemeente|dienst|dag wijst precies
// één cel aan.
//
// Er is bewust geen bestaande helper hergebruikt: roostercodes.js:425 (getCel) en
// useRoosterStore.js:469 (wijzigCel) lopen wel over dezelfde structuur, maar zoeken
// allebei ÉÉN cel op in plaats van te tellen.
function telRoostercellen(naam, vastgelegdeWeken, maandroosters) {
  if (!naam) return 0;
  const gezien = new Set();
  const telWeek = (week) => {
    const rooster = week?.rooster;
    if (!rooster) return;
    const weekId = `${week.isoJaar ?? ''}-${week.weeknummer ?? ''}`;
    for (const gemKey of Object.keys(rooster)) {
      for (const dienstId of Object.keys(rooster[gemKey] || {})) {
        for (const dag of Object.keys(rooster[gemKey][dienstId] || {})) {
          const cel = rooster[gemKey][dienstId][dag];
          if (Array.isArray(cel) && cel.includes(naam)) {
            gezien.add(`${weekId}|${gemKey}|${dienstId}|${dag}`);
          }
        }
      }
    }
  };
  for (const entry of Object.values(vastgelegdeWeken || {})) telWeek(entry?.week);
  for (const mr of Object.values(maandroosters || {})) {
    for (const w of (mr?.weken || [])) telWeek(w);
  }
  return gezien.size;
}

export default function MedewerkerModal({ mwKey, onSluit }) {
  const { regels, updateMedewerker, addMedewerker, vastgelegdeWeken, maandroosters } = useRoosterStore();
  const isNieuw = !mwKey;
  const bestaand = mwKey ? regels.medewerkers[mwKey] : null;

  // Beschikbaarheid wordt nu per dag in 3 standen bewerkt en terugvertaald naar vaste_vrije_dagen
  // + geen_late_dienst_dagen. De oude werkdagen-whitelist vouwen we bij openen in: een dag die NIET
  // in een gevulde werkdagen-lijst staat, telt als Vrij (bv. Bram werkdagen=[vr,za,zo] → ma/di/wo/do vrij).
  const initVrijeDagen = (() => {
    const vrij = new Set(bestaand?.vaste_vrije_dagen || []);
    if (bestaand?.werkdagen?.length) {
      for (const d of DAGEN) if (!bestaand.werkdagen.includes(d)) vrij.add(d);
    }
    return DAGEN.filter(d => vrij.has(d));
  })();

  const [form, setForm] = useState(
    bestaand
      ? { ...bestaand, rollen: bestaand.rollen ?? ['medewerker'], vaste_vrije_dagen: initVrijeDagen, werkdagen: [] }
      : { ...DEFAULT_MEDEWERKER, werkdagen: [] }
  );
  const [nieuweAfspraak, setNieuweAfspraak] = useState('');

  const gemeenteOpties = Object.entries(regels.gemeenten)
    .filter(([k]) => !k.startsWith('_'))
    .map(([k, g]) => ({ key: k, naam: g.naam }));

  function setVeld(veld, val) {
    setForm(prev => ({ ...prev, [veld]: val }));
  }

  // Huidige stand van een dag, afgeleid uit de config-velden.
  const standVan = (d) =>
    (form.vaste_vrije_dagen || []).includes(d) ? 'vrij'
      : (form.geen_late_dienst_dagen || []).includes(d) ? 'alleen_dag'
      : (form.geen_dagdienst_dagen || []).includes(d) ? 'alleen_laat'
      : 'beschikbaar';

  // Roteer de dag-stand (Beschikbaar → Vrij → Alleen dag → Alleen laat) en schrijf terug naar de drie config-velden.
  function roteerDag(d) {
    const volgende = STAND_VOLGORDE[standVan(d)];
    const vrij = new Set(form.vaste_vrije_dagen || []);
    const geenLate = new Set(form.geen_late_dienst_dagen || []);
    const geenDag = new Set(form.geen_dagdienst_dagen || []);
    vrij.delete(d);
    geenLate.delete(d);
    geenDag.delete(d);
    if (volgende === 'vrij') vrij.add(d);
    if (volgende === 'alleen_dag') geenLate.add(d);
    if (volgende === 'alleen_laat') geenDag.add(d);
    setForm(prev => ({
      ...prev,
      vaste_vrije_dagen: DAGEN.filter(x => vrij.has(x)),
      geen_late_dienst_dagen: DAGEN.filter(x => geenLate.has(x)),
      geen_dagdienst_dagen: DAGEN.filter(x => geenDag.has(x)),
    }));
  }

  function slaOp() {
    // TRIM OP DE NAAM. De guard hieronder testte de getrimde waarde al op leegte, maar het
    // data-object onderaan schreef `...form` weg en dus de ONGETRIMDE naam. "Joerie " kwam
    // daar ongeschonden doorheen: na trim niet leeg, dus geen enkele guard sloeg aan, en de
    // spatie ging mee de store in. Vanaf hier is `naam` de waarde die ook opgeslagen wordt —
    // dezelfde behandeling die het e-mailveld in datzelfde object al kreeg.
    const naam = form.naam.trim();
    if (!naam) return;

    // HERNOEMEN LOSKOPPELEN-WAARSCHUWING. Het rooster bewaart medewerkers op hun
    // weergavenaam, dus na een hernoeming blijft de OUDE naam in de bewaarde weken
    // staan. analyseer-week.js:84-85 doet `const k = naamNaarKey[naam]; if (k) ...` en
    // slaat een onbekende celnaam STIL over: bij de eerstvolgende hercalculatie van zo'n
    // week (elke wijzigCel doet er een, useRoosterStore.js:478-485) verdwijnen de uren
    // van die diensten uit de urenbalans zonder één melding. Daarom hier vóóraf vragen.
    //
    // Alleen bij bewerken (een nieuwe medewerker heeft geen oude naam) en alleen als er
    // echt cellen op de oude naam staan — anders is er niets te verliezen en zou de
    // confirm alleen maar in de weg zitten.
    const oudeNaam = bestaand?.naam;
    if (!isNieuw && oudeNaam && oudeNaam !== naam) {
      const cellen = telRoostercellen(oudeNaam, vastgelegdeWeken, maandroosters);
      if (cellen > 0) {
        const akkoord = window.confirm(
          `"${oudeNaam}" komt voor in ${cellen} ${cellen === 1 ? 'roostercel' : 'roostercellen'}.\n\n`
          + `Na hernoemen naar "${naam}" zijn die diensten niet meer aan deze medewerker `
          + 'gekoppeld en verdwijnen zijn uren uit de urenbalans.\n\n'
          + 'Doorgaan met hernoemen?',
        );
        if (!akkoord) return; // annuleren = niets opslaan, modal blijft open
      }
    }

    // werkdagen-whitelist wordt niet meer via de UI bewerkt (bij openen gevouwen in vaste_vrije_dagen);
    // leeg = geen restrictie, dus altijd leeg opslaan.
    // rollen leeg = geen spookaccount: val terug op medewerker.
    const rollen = form.rollen?.length ? form.rollen : ['medewerker'];
    const data = { ...form, rollen, werkdagen: [], naam, email: (form.email || '').trim() };
    if (isNieuw) {
      addMedewerker(data);
    } else {
      updateMedewerker(mwKey, data);
    }
    onSluit();
  }

  function voegAfspraakToe() {
    const tekst = nieuweAfspraak.trim();
    if (!tekst) return;
    setForm(prev => ({ ...prev, afspraken: [...(prev.afspraken || []), tekst] }));
    setNieuweAfspraak('');
  }

  function verwijderAfspraak(i) {
    setForm(prev => ({ ...prev, afspraken: prev.afspraken.filter((_, idx) => idx !== i) }));
  }

  const heeftMeerdereGemeenten = (form.gemeenten || []).length >= 2;
  const isMedewerker = form.rollen?.includes('medewerker');

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h2 className="text-lg font-semibold text-slate-800">
            {isNieuw ? 'Nieuwe medewerker' : `Bewerk: ${bestaand?.naam}`}
          </h2>
          <button onClick={onSluit} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Formulier */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* 1. Naam + contracturen */}
          <div className={`grid ${isMedewerker ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Naam</label>
              <input
                type="text"
                value={form.naam}
                onChange={e => setVeld('naam', e.target.value)}
                placeholder="Voornaam"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            {isMedewerker && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Contracturen / week
              </label>
              <input
                type="number"
                min="4"
                max="40"
                step="4"
                value={form.contract_uren}
                onChange={e => setVeld('contract_uren', Number(e.target.value))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            )}
          </div>

          {/* 1b. E-mailadres (werk-e-mail, optioneel — koppelt de Microsoft-login aan deze medewerker) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-mailadres</label>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={e => setVeld('email', e.target.value)}
              placeholder="voornaam@straatschoolveiligheid.nl"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* 1c. Rol — combineerbaar; 'medewerker' = wordt ingeroosterd, 'teamleider' = beheer-toegang */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Rol</label>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.rollen.includes('medewerker')}
                onChange={e => {
                  const huidig = form.rollen || [];
                  const nieuw = e.target.checked ? [...huidig, 'medewerker'] : huidig.filter(r => r !== 'medewerker');
                  setVeld('rollen', nieuw);
                }}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-teal-600 shrink-0"
              />
              <span className="text-sm text-slate-700">
                Medewerker <span className="text-slate-500">(wordt ingeroosterd)</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.rollen.includes('teamleider')}
                onChange={e => {
                  const huidig = form.rollen || [];
                  const nieuw = e.target.checked ? [...huidig, 'teamleider'] : huidig.filter(r => r !== 'teamleider');
                  setVeld('rollen', nieuw);
                }}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-teal-600 shrink-0"
              />
              <span className="text-sm text-slate-700">
                Teamleider <span className="text-slate-500">(beheer-toegang)</span>
              </span>
            </label>
          </div>

          {isMedewerker && (
            <>
          {/* 2. Gemeenten */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Inzetbaar in gemeente(n)
            </label>
            <div className="flex gap-4 flex-wrap">
              {gemeenteOpties.map(g => (
                <label key={g.key} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.gemeenten?.includes(g.key) || false}
                    onChange={e => {
                      const huidig = form.gemeenten || [];
                      const nieuw = e.target.checked
                        ? [...huidig, g.key]
                        : huidig.filter(x => x !== g.key);
                      setVeld('gemeenten', nieuw);
                      if (!nieuw.includes(form.voorkeur_gemeente)) setVeld('voorkeur_gemeente', null);
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-brand-700"
                  />
                  <span className="text-sm text-slate-700 capitalize">{g.naam}</span>
                </label>
              ))}
            </div>
            {heeftMeerdereGemeenten && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Voorkeur gemeente:</span>
                  <select
                    value={form.voorkeur_gemeente || ''}
                    onChange={e => {
                      setVeld('voorkeur_gemeente', e.target.value || null);
                      if (!e.target.value) setVeld('vast_rotatiepatroon', false);
                    }}
                    className="border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    <option value="">Geen voorkeur</option>
                    {(form.gemeenten || []).map(gKey => (
                      <option key={gKey} value={gKey}>
                        {regels.gemeenten[gKey]?.naam || gKey}
                      </option>
                    ))}
                  </select>
                </div>
                {form.voorkeur_gemeente && (
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.vast_rotatiepatroon}
                      onChange={e => setVeld('vast_rotatiepatroon', e.target.checked)}
                      className="w-4 h-4 mt-0.5 rounded border-slate-300 text-teal-600 shrink-0"
                    />
                    <span className="text-sm text-slate-700">
                      Vast rotatiepatroon —{' '}
                      <span className="text-slate-500">
                        altijd ingepland in {regels.gemeenten[form.voorkeur_gemeente]?.naam} op hun dienstdagen;
                        geblokkeerd voor andere gemeenten die dagen
                      </span>
                    </span>
                  </label>
                )}
              </div>
            )}
          </div>

          {/* 3. Beschikbaarheid */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Beschikbaarheid</div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-0.5">Beschikbaarheid per dag</div>
              <div className="text-xs text-slate-400 mb-1.5">
                Klik een dag om te wisselen: dag+laat → vrij → alleen dag → alleen laat (bijv. Danny: Wo vrij, Myrthe: Ma alleen dag).
              </div>
              <DagStandKiezer standVan={standVan} onRoteer={roteerDag} />
            </div>

            <Toggle
              aan={!!form.weekend_rotatie}
              onChange={v => setVeld('weekend_rotatie', v)}
              label="Weekend-rotatie"
              hint="Aan = om-het-weekend vrij. Uit = werkt elk weekend (bijv. Jermaine)."
            />

            {/* Bij een vast rotatiepatroon is deze toggle dood: isBeschikbaar leest de
                weekend-rotatie alleen bij weekend_rotatie === true, en het weekend komt dan uit
                de dienstcyclus van de voorkeur-gemeente. Zonder deze melding lijkt de stand van
                de toggle iets te betekenen wat hij niet doet. */}
            {form.vast_rotatiepatroon && form.voorkeur_gemeente && (
              <div className="mt-1 text-xs text-violet-700">
                Let op: deze toggle heeft geen effect zolang &lsquo;Vast rotatiepatroon&rsquo; aanstaat.
                Het weekend volgt dan de dienstdagen van {regels.gemeenten[form.voorkeur_gemeente]?.naam || form.voorkeur_gemeente}.
              </div>
            )}

            {form.weekend_rotatie && (
              <div className="mt-2 pl-1">
                <div className="text-xs text-slate-500 mb-1">Weekend-groep:</div>
                <select
                  value={form.weekend_groep || 'A'}
                  onChange={e => setVeld('weekend_groep', e.target.value)}
                  className="border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                >
                  <option value="A">Groep A — vrij in even weken</option>
                  <option value="B">Groep B — vrij in oneven weken</option>
                </select>
                <div className="text-xs text-slate-400 mt-1">Bepaalt welk weekend vrij is; zo valt niet iedereen hetzelfde weekend weg.</div>
              </div>
            )}
          </div>

          {/* 5. Kantoor & School */}
          <div className="bg-slate-50 rounded-xl p-4 space-y-4">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Kantoor & School</div>

            <DagToggle
              label="Kantoordag(en)"
              hint="Die dag werkt de persoon op kantoor — niet op straat. Telt wel als gewerkte uren."
              waarde={form.kantoor_dagen || []}
              onChange={v => setVeld('kantoor_dagen', v)}
            />

            <div>
              <Toggle
                aan={!!form.school}
                onChange={v => setVeld('school', v
                  ? { dag: 'ma', uren: 8, regeling: 'afwisselend_werktijd_eigentijd' }
                  : null
                )}
                label="School op vaste dag"
                hint="Deze dag nooit op straat. Schooluren tellen om de week mee als werktijd: de ene week 8u werk, de week erna in eigen tijd."
              />
              {form.school && (
                <div className="mt-2 pl-1">
                  <div className="text-xs text-slate-500 mb-1">Schooldag:</div>
                  <DagSingle
                    waarde={form.school.dag}
                    onChange={dag => dag && setVeld('school', { ...form.school, dag })}
                  />
                  <div className="mt-2">
                    <div className="text-xs text-slate-500 mb-1">Schooluren (tellen om de week mee):</div>
                    <input
                      type="number"
                      min="0"
                      max="40"
                      step="1"
                      value={form.school.uren ?? 8}
                      onChange={e => setVeld('school', { ...form.school, uren: Number(e.target.value) })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 6. Opmerkingen */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-0.5">
              Opmerkingen
            </label>
            <p className="text-xs text-slate-400 mb-2">
              Alleen zichtbaar voor de roosteraar — beïnvloedt de engine niet.
            </p>
            <div className="space-y-1.5 mb-2">
              {(form.afspraken || []).map((a, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                  <span className="flex-1 text-sm text-slate-700">{a}</span>
                  <button
                    type="button"
                    onClick={() => verwijderAfspraak(i)}
                    className="text-slate-300 hover:text-red-500 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={nieuweAfspraak}
                onChange={e => setNieuweAfspraak(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && voegAfspraakToe()}
                placeholder="Bijv. belt liever niet na 20:00, vast aanspreekpunt Lingewaard..."
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <button
                type="button"
                onClick={voegAfspraakToe}
                className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
          <button
            onClick={onSluit}
            className="flex-1 py-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
          >
            Annuleren
          </button>
          <button
            onClick={slaOp}
            disabled={!form.naam.trim() || (form.gemeenten || []).length === 0}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-brand-700 hover:bg-brand-800 disabled:opacity-40 transition"
          >
            {isNieuw ? 'Toevoegen' : 'Opslaan'}
          </button>
        </div>

      </div>
    </div>
  );
}
